// src/hooks/useHotelScope.ts
//
// Multi-hotel scope (Decision 6 / mockup 05 callout 7): every list and
// analytics page lets the user pick all hotels, one hotel, or several from a
// checklist. This hook owns the fetch of the hotels/groups the current user
// may see, the current selection (persisted through the preferences store,
// falling back to localStorage the same way the store already does), and
// translates the selection into request parameters:
//
// - a subset (or the whole scope explicitly re-ticked) -> `hotelIds` (comma list)
// - the untouched "all hotels of my scope" default -> the legacy `hotelId` /
//   `groupId` params, so endpoints not yet extended to `hotelIds` keep working.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDataProvider } from 'react-admin';
import { getAssignedGroups, getAssignedHotels, getCurrentGroup, getCurrentHotel, getCurrentUser } from '@/utils/auth';
import { usePreferences } from '@/providers/preferencesProvider';
import type { Group, Hotel } from '@/types/models';

export interface ScopeGroup {
  id: string;
  name: string;
  hotels: Hotel[];
}

export interface HotelScope {
  loading: boolean;
  /** Groups (with their hotels) the current user is authorized to see. Single-group roles get one entry. */
  groups: ScopeGroup[];
  /** Flat list of every hotel in scope, for search. */
  allHotels: Hotel[];
  /** `null` = every hotel in scope ("Todos los hoteles"). */
  selectedHotelIds: string[] | null;
  setSelectedHotelIds: (ids: string[] | null) => void;
  /** Header pill i18n: { key, params }. */
  summary: { key: string; params?: Record<string, unknown> };
  /** Request params — spread into a dataProvider filter/meta object. */
  scopeParams: { hotelIds: string } | { hotelId?: string; groupId?: string };
}

/**
 * The `hotelIds` request value of a selection; '' for «every hotel in scope». Equal selections
 * give the same string, whatever array they arrive in.
 */
export const hotelSelectionKey = (ids: readonly string[] | null | undefined): string =>
  ids && ids.length > 0 ? ids.join(',') : '';

export const useHotelScope = (): HotelScope => {
  const dataProvider = useDataProvider();
  const { preferences, setPreferences } = usePreferences();
  const user = getCurrentUser();

  const [groups, setGroups] = useState<ScopeGroup[]>([]);
  const [loading, setLoading] = useState(true);

  const assignedGroups = useMemo(() => getAssignedGroups(), []);
  const assignedHotels = useMemo(() => getAssignedHotels(), []);
  const isSuperAdmin = user?.role === 'SuperAdmin' || user?.role === 'Admin';

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const groupFilter: Record<string, unknown> = {};
        if (!isSuperAdmin && assignedGroups.length > 0) {
          groupFilter.id = assignedGroups;
        }

        const groupsResponse = isSuperAdmin || assignedGroups.length > 0
          ? await dataProvider.getList('groups', {
              pagination: { page: 1, perPage: 200 },
              sort: { field: 'name', order: 'ASC' },
              filter: groupFilter,
            })
          : { data: [] as Group[] };

        const hotelFilter: Record<string, unknown> = {};
        if (!isSuperAdmin && assignedGroups.length === 0 && assignedHotels.length > 0) {
          hotelFilter.id = assignedHotels;
        }

        const hotelsResponse = await dataProvider.getList('hotels', {
          pagination: { page: 1, perPage: 500 },
          sort: { field: 'name', order: 'ASC' },
          filter: hotelFilter,
        });

        if (cancelled) return;

        const hotels = hotelsResponse.data as Hotel[];
        const fetchedGroups = groupsResponse.data as Group[];

        const scopeGroups: ScopeGroup[] = fetchedGroups.length > 0
          ? fetchedGroups.map((g) => ({
              id: g.id,
              name: g.name,
              hotels: hotels.filter((h) => h.groupId === g.id),
            }))
          : [{ id: user?.groupId || 'default', name: user?.groupId || '', hotels }];

        setGroups(scopeGroups);
      } catch {
        setGroups([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin, assignedGroups.join(','), assignedHotels.join(',')]);

  const allHotels = useMemo(() => groups.flatMap((g) => g.hotels), [groups]);

  // An empty selection (the old «Ninguno») is not a scope: read it as every hotel, which is
  // what the screens showed for it anyway.
  const storedHotelIds = preferences.scope.hotelIds;
  const selectedHotelIds = storedHotelIds && storedHotelIds.length > 0 ? storedHotelIds : null;
  // `onlyWithArrivals` stays in the stored preference for compatibility but is no longer offered.
  const storedOnlyWithArrivals = preferences.scope.onlyWithArrivals;

  const setSelectedHotelIds = useCallback(
    (ids: string[] | null) => {
      setPreferences({ scope: { hotelIds: ids, onlyWithArrivals: storedOnlyWithArrivals } });
    },
    [setPreferences, storedOnlyWithArrivals]
  );

  const summary = useMemo(() => {
    if (!selectedHotelIds) return { key: 'scope.pill.all' };
    if (selectedHotelIds.length === 1) return { key: 'scope.pill.one' };
    if (selectedHotelIds.length === allHotels.length && allHotels.length > 0) {
      return { key: 'scope.pill.all' };
    }
    return { key: 'scope.pill.many', params: { count: selectedHotelIds.length } };
  }, [selectedHotelIds, allHotels.length]);

  // Keyed by the selection's content, not its array: the stored preferences are read from the
  // browser first and then from the server, which hands back an equal but new array. Keyed by
  // identity, every screen fetched its data twice on load (the first answer thrown away).
  const selectedHotelIdsKey = hotelSelectionKey(selectedHotelIds);
  const scopeParams = useMemo((): HotelScope['scopeParams'] => {
    if (selectedHotelIdsKey) {
      return { hotelIds: selectedHotelIdsKey };
    }
    // Legacy fallback: a single assigned hotel, or the current group/hotel from the token.
    const fallbackHotelId = assignedHotels.length === 1 ? assignedHotels[0] : getCurrentHotel() || undefined;
    const fallbackGroupId = getCurrentGroup() || undefined;
    return { hotelId: fallbackHotelId, groupId: fallbackHotelId ? undefined : fallbackGroupId };
  }, [selectedHotelIdsKey, assignedHotels]);

  return {
    loading,
    groups,
    allHotels,
    selectedHotelIds,
    setSelectedHotelIds,
    summary,
    scopeParams,
  };
};
